import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import Debug from 'debug';

const debug = Debug('aumico:aumico-components:HamTiptapToolbarComponent');
const STICKY_TOP_OFFSET_SELECTOR = 'inner-page-tab-navigation';

@Component({
    selector: 'ham-tiptap-toolbar',
    templateUrl: './tiptap-toolbar.component.html',
    styleUrls: ['./tiptap-toolbar.component.scss'],
})
export class HamTiptapToolbarComponent implements OnInit, OnDestroy {
    @Input() editor: any | null = null;
    @Input() headerOffsetSelector: string | null = STICKY_TOP_OFFSET_SELECTOR;
    @Input() headerOffsetPx = 0;
    @Input() disableSticky = false;
    @Output() imageSelected = new EventEmitter<File>();

    @ViewChild('imageInput') imageInput?: ElementRef<HTMLInputElement>;
    @ViewChild('toolbarElement') toolbarElement?: ElementRef<HTMLDivElement>;
    @ViewChild('sentinel') sentinelElement?: ElementRef<HTMLDivElement>;
    @ViewChild('surfaceElement') surfaceElement?: ElementRef<HTMLDivElement>;
    @ViewChild('tableToolbarElement') tableToolbarElement?: ElementRef<HTMLDivElement>;

    isParagraphMenuOpen = false;
    isAlignMenuOpen = false;
    isLineHeightMenuOpen = false;
    isImageSizeMenuOpen = false;
    isCellAlignMenuOpen = false;
    isCellVerticalMenuOpen = false;
    isColumnMenuOpen = false;
    isRowMenuOpen = false;
    isColumnStyleMenuOpen = false;
    isRowStyleMenuOpen = false;

    isSticky = false;
    isToolbarVisible = true;
    toolbarWidth = 'auto';
    toolbarTop = '0px';
    tableToolbarTop = 0;
    isTableToolbarVisible = false;
    isTableToolbarSticky = false;
    tableToolbarStickyTop = '0px';
    tableToolbarStickyLeft = '0px';
    tableToolbarStickyWidth = 'auto';
    private editorHasFocus = false;
    private intersectionObserver?: IntersectionObserver;
    private editorVisibilityObserver?: IntersectionObserver;
    private focusListener?: () => void;
    private blurListener?: () => void;
    private selectionUpdateListener?: () => void;
    private isInteractingWithToolbar = false;
    private scrollContainer?: HTMLElement | Window;
    private scrollListener?: () => void;
    private scrollRafId?: number;
    private tableToolbarRafId?: number;
    private tableToolbarHideTimer?: number;
    private readonly tableToolbarGap = 6;
    private editorElement?: HTMLElement | null;
    private headerElement?: HTMLElement | null;
    private headerLookupTimer?: number;
    private headerLookupAttempts = 0;
    private readonly headerLookupMaxAttempts = 12;
    private readonly headerLookupDelayMs = 250;
    private headerSelectorUsed?: string;

    tiptapUndo(): void {
        this.focusEditor();
        this.editor?.commands?.undo?.();
    }

    tiptapRedo(): void {
        this.focusEditor();
        this.editor?.commands?.redo?.();
    }

    tiptapToggleBold(): void {
        this.focusEditor();
        this.editor?.commands?.toggleBold?.();
    }

    tiptapToggleItalic(): void {
        this.focusEditor();
        this.editor?.commands?.toggleItalic?.();
    }

    tiptapToggleUnderline(): void {
        this.focusEditor();
        this.editor?.commands?.toggleUnderline?.();
    }

    tiptapToggleStrike(): void {
        this.focusEditor();
        this.editor?.commands?.toggleStrike?.();
    }

    tiptapRemoveFormat(): void {
        this.focusEditor();
        this.editor?.commands?.clearNodes?.();
        this.editor?.commands?.unsetAllMarks?.();
    }

    tiptapToggleBlockquote(): void {
        this.focusEditor();
        this.editor?.commands?.toggleBlockquote?.();
    }

    tiptapToggleHeading(level: 1 | 2 | 3 | 4 | 5 | 6 = 2): void {
        this.focusEditor();
        this.editor?.commands?.toggleHeading?.({ level });
    }

    tiptapSetAlign(value: 'left' | 'center' | 'right' | 'justify'): void {
        this.focusEditor();
        this.editor?.commands?.setTextAlign?.(value);
    }

    tiptapSetLineHeight(value: number): void {
        this.focusEditor();
        this.editor?.commands?.setLineHeight?.(value);
    }

    tiptapUnsetLineHeight(): void {
        this.focusEditor();
        this.editor?.commands?.unsetLineHeight?.();
    }

    tiptapToggleBulletList(): void {
        this.focusEditor();
        this.editor?.commands?.toggleBulletList?.();
    }

    tiptapToggleOrderedList(): void {
        this.focusEditor();
        this.editor?.commands?.toggleOrderedList?.();
    }

    tiptapIndent(): void {
        this.focusEditor();
        this.editor?.commands?.indent?.();
    }

    tiptapOutdent(): void {
        this.focusEditor();
        this.editor?.commands?.outdent?.();
    }

    tiptapInsertImage(): void {
        this.focusEditor();
        this.imageInput?.nativeElement?.click();
    }

    tiptapSetImageSize(size: 'auto' | '25' | '50' | '75' | '100'): void {
        if (!this.editor || !this.editor.isActive?.('image')) {
            return;
        }

        const width = size === 'auto' ? null : `${size}%`;
        const style = size === 'auto' ? null : `width: ${size}%; height: auto;`;

        this.focusEditor();
        this.editor?.commands?.updateAttributes?.('image', {
            width,
            height: null,
            style,
        });
        this.isImageSizeMenuOpen = false;
    }

    tiptapInsertPageBreak(): void {
        this.focusEditor();
        this.editor?.commands?.insertPageBreak?.();
    }

    tiptapInsertTabSpan(): void {
        this.focusEditor();
        this.editor?.commands?.insertTabSpan?.();
    }

    tiptapInsertTable(): void {
        this.focusEditor();
        this.editor?.commands?.insertTable?.({ rows: 3, cols: 3, withHeaderRow: true });
    }

    tiptapSelectAll(): void {
        this.focusEditor();
        this.editor?.commands?.selectAll?.();
    }

    tiptapToggleHeaderRow(): void {
        this.focusEditor();
        this.editor?.commands?.toggleHeaderRow?.();
    }

    tiptapToggleHeaderColumn(): void {
        this.focusEditor();
        this.editor?.commands?.toggleHeaderColumn?.();
    }

    tiptapAddColumnBefore(): void {
        this.focusEditor();
        this.editor?.commands?.addColumnBefore?.();
        this.isColumnMenuOpen = false;
    }

    tiptapAddColumnAfter(): void {
        this.focusEditor();
        this.editor?.commands?.addColumnAfter?.();
        this.isColumnMenuOpen = false;
    }

    tiptapDeleteColumn(): void {
        this.focusEditor();
        this.editor?.commands?.deleteColumn?.();
        this.isColumnMenuOpen = false;
    }

    tiptapAddRowBefore(): void {
        this.focusEditor();
        this.editor?.commands?.addRowBefore?.();
        this.isRowMenuOpen = false;
    }

    tiptapAddRowAfter(): void {
        this.focusEditor();
        this.editor?.commands?.addRowAfter?.();
        this.isRowMenuOpen = false;
    }

    tiptapDeleteRow(): void {
        this.focusEditor();
        this.editor?.commands?.deleteRow?.();
        this.isRowMenuOpen = false;
    }

    tiptapMergeCells(): void {
        this.focusEditor();
        this.editor?.commands?.mergeCells?.();
    }

    tiptapSplitCell(): void {
        this.focusEditor();
        this.editor?.commands?.splitCell?.();
    }

    tiptapDeleteTable(): void {
        this.focusEditor();
        this.editor?.commands?.deleteTable?.();
    }

    tiptapSetCellAlign(value: 'left' | 'center' | 'right' | null): void {
        this.focusEditor();
        this.editor?.commands?.setTableCellTextAlign?.(value);
        this.isCellAlignMenuOpen = false;
    }

    tiptapSetCellVerticalAlign(value: 'top' | 'middle' | 'bottom' | null): void {
        this.focusEditor();
        this.editor?.commands?.setTableCellVerticalAlign?.(value);
        this.isCellVerticalMenuOpen = false;
    }

    tiptapColumnTotal(): void {
        this.focusEditor();
        this.editor?.commands?.setColumnStyle?.('column-Total');
        this.isColumnStyleMenuOpen = false;
    }

    tiptapColumnHighlight(): void {
        this.focusEditor();
        this.editor?.commands?.setColumnStyle?.('column-Highlight');
        this.isColumnStyleMenuOpen = false;
    }

    tiptapColumnNumber(): void {
        this.focusEditor();
        this.editor?.commands?.setColumnStyle?.('column-number');
        this.isColumnStyleMenuOpen = false;
    }

    tiptapClearColumnStyles(): void {
        this.focusEditor();
        this.editor?.commands?.clearColumnStyles?.();
        this.isColumnStyleMenuOpen = false;
    }

    tiptapRowTotal(): void {
        this.focusEditor();
        this.editor?.commands?.setRowStyle?.('row-Total');
        this.isRowStyleMenuOpen = false;
    }

    tiptapClearRowStyles(): void {
        this.focusEditor();
        this.editor?.commands?.clearRowStyles?.();
        this.isRowStyleMenuOpen = false;
    }

    tiptapToggleImageSizeMenu(): void {
        this.isImageSizeMenuOpen = !this.isImageSizeMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    tiptapToggleCellAlignMenu(): void {
        this.isCellAlignMenuOpen = !this.isCellAlignMenuOpen;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    tiptapToggleCellVerticalMenu(): void {
        this.isCellVerticalMenuOpen = !this.isCellVerticalMenuOpen;
        this.isCellAlignMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    toggleColumnMenu(): void {
        this.isColumnMenuOpen = !this.isColumnMenuOpen;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    toggleRowMenu(): void {
        this.isRowMenuOpen = !this.isRowMenuOpen;
        this.isColumnMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    toggleColumnStyleMenu(): void {
        this.isColumnStyleMenuOpen = !this.isColumnStyleMenuOpen;
        this.isRowStyleMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    toggleRowStyleMenu(): void {
        this.isRowStyleMenuOpen = !this.isRowStyleMenuOpen;
        this.isColumnStyleMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
    }

    toggleParagraphMenu(): void {
        this.isParagraphMenuOpen = !this.isParagraphMenuOpen;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    toggleAlignMenu(): void {
        this.isAlignMenuOpen = !this.isAlignMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    toggleLineHeightMenu(): void {
        this.isLineHeightMenuOpen = !this.isLineHeightMenuOpen;
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    closeMenus(): void {
        this.isParagraphMenuOpen = false;
        this.isAlignMenuOpen = false;
        this.isLineHeightMenuOpen = false;
        this.isImageSizeMenuOpen = false;
        this.isCellAlignMenuOpen = false;
        this.isCellVerticalMenuOpen = false;
        this.isColumnMenuOpen = false;
        this.isRowMenuOpen = false;
        this.isColumnStyleMenuOpen = false;
        this.isRowStyleMenuOpen = false;
    }

    isInTable(): boolean {
        return !!(this.editor && this.editor.isActive?.('table'));
    }

    onImageSelected(event: Event): void {
        const input = event.target as HTMLInputElement | null;
        if (!input?.files?.length) {
            return;
        }

        const [file] = Array.from(input.files);
        input.value = '';

        if (!file.type.startsWith('image/')) {
            return;
        }

        this.imageSelected.emit(file);
    }

    onSurfaceClick(event: MouseEvent): void {
        if (!this.editor) {
            return;
        }

        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const inEditor = target.closest('.tiptap-editor');
        const inProseMirror = target.closest('.ProseMirror');
        if (inEditor && !inProseMirror) {
            this.editor?.commands?.focus?.('start');
        }
    }

    private focusEditor(): void {
        this.editor?.commands?.focus?.();
    }

    ngOnInit(): void {
        debug('ngOnInit: start sticky setup');
        this.setupStickyBehavior();
    }

    ngOnDestroy(): void {
        debug('ngOnDestroy: cleanup sticky setup');
        this.cleanupStickyBehavior();
    }

    private setupStickyBehavior(): void {
        if (this.disableSticky) {
            debug('setupStickyBehavior: skipped (disableSticky=true)');
            return;
        }
        // Wait for editor to be initialized
        setTimeout(() => {
            debug('setupStickyBehavior: start', {
                hasEditor: !!this.editor,
                hasToolbar: !!this.toolbarElement,
                hasSentinel: !!this.sentinelElement,
            });
            if (!this.editor || !this.toolbarElement || !this.sentinelElement) {
                debug('setupStickyBehavior: missing editor/toolbar/sentinel, skipping');
                return;
            }

            const editorElement = this.getEditorElement();
            if (!editorElement) {
                debug('setupStickyBehavior: editor element not found');
                return;
            }
            this.editorElement = editorElement;
            debug('setupStickyBehavior: editor element found', {
                tagName: editorElement.tagName,
                className: editorElement.className,
            });

            // Track editor focus state
            this.focusListener = () => {
                this.editorHasFocus = true;
                debug('focus: editorHasFocus=true');
                this.updateStickyFromScroll('focus');
                this.updateTableToolbarPosition('focus');
            };

            this.blurListener = () => {
                if (this.isInteractingWithToolbar) {
                    debug('blur: ignored due to toolbar interaction');
                    return;
                }
                this.editorHasFocus = false;
                debug('blur: editorHasFocus=false');
                this.updateStickyFromScroll('blur');
                this.updateTableToolbarPosition('blur');
            };

            editorElement.addEventListener('focus', this.focusListener, true);
            editorElement.addEventListener('blur', this.blurListener, true);

            this.selectionUpdateListener = () => {
                this.updateTableToolbarPosition('selection');
            };
            this.editor?.on?.('selectionUpdate', this.selectionUpdateListener);

            // Setup Intersection Observer to detect when toolbar scrolls out of view
            this.setupIntersectionObserver();
            // Fallback: also track scroll events on the container
            this.setupScrollListener();
            this.setupEditorVisibilityObserver();
            this.updateTableToolbarPosition('init');
            const selector = this.headerOffsetSelector?.trim();
            if (selector) {
                this.scheduleHeaderLookup(selector);
            }
        }, 100);
    }

    private cleanupStickyBehavior(): void {
        debug('cleanupStickyBehavior: start', {
            hasFocusListener: !!this.focusListener,
            hasBlurListener: !!this.blurListener,
            hasObserver: !!this.intersectionObserver,
        });
        if (this.focusListener || this.blurListener) {
            const editorElement = this.getEditorElement();
            if (editorElement) {
                if (this.focusListener) {
                    editorElement.removeEventListener('focus', this.focusListener, true);
                }
                if (this.blurListener) {
                    editorElement.removeEventListener('blur', this.blurListener, true);
                }
            }
        }

        if (this.selectionUpdateListener) {
            this.editor?.off?.('selectionUpdate', this.selectionUpdateListener);
            this.selectionUpdateListener = undefined;
        }

        if (this.intersectionObserver) {
            this.intersectionObserver.disconnect();
            this.intersectionObserver = undefined;
        }

        if (this.editorVisibilityObserver) {
            this.editorVisibilityObserver.disconnect();
            this.editorVisibilityObserver = undefined;
        }

        if (this.scrollListener && this.scrollContainer) {
            const target = this.scrollContainer instanceof Window ? window : this.scrollContainer;
            target.removeEventListener('scroll', this.scrollListener);
            this.scrollListener = undefined;
        }

        if (this.scrollRafId) {
            cancelAnimationFrame(this.scrollRafId);
            this.scrollRafId = undefined;
        }

        if (this.tableToolbarRafId) {
            cancelAnimationFrame(this.tableToolbarRafId);
            this.tableToolbarRafId = undefined;
        }

        if (this.tableToolbarHideTimer) {
            window.clearTimeout(this.tableToolbarHideTimer);
            this.tableToolbarHideTimer = undefined;
        }

        if (this.headerLookupTimer) {
            window.clearTimeout(this.headerLookupTimer);
            this.headerLookupTimer = undefined;
        }
    }

    private getEditorElement(): HTMLElement | null {
        if (!this.toolbarElement) {
            debug('getEditorElement: missing toolbarElement');
            return null;
        }
        const surface = this.toolbarElement.nativeElement.nextElementSibling;
        const editorElement = surface?.querySelector('.tiptap-editor, .ProseMirror') as HTMLElement;
        debug('getEditorElement: result', {
            hasSurface: !!surface,
            found: !!editorElement,
        });
        return editorElement;
    }

    private setupIntersectionObserver(): void {
        if (!this.sentinelElement || !this.toolbarElement) {
            debug('setupIntersectionObserver: missing sentinel/toolbar');
            return;
        }

        // Find the scrolling container
        const scrollContainer = this.findScrollContainer(this.toolbarElement.nativeElement);
        this.scrollContainer = scrollContainer === document.body ? window : scrollContainer;
        debug('setupIntersectionObserver: scroll container', {
            tagName: scrollContainer?.tagName,
            className: scrollContainer?.className,
            isBody: scrollContainer === document.body,
        });

        // Create Intersection Observer to watch the sentinel element
        // When the sentinel goes out of view (scrolled past), make toolbar sticky
        this.intersectionObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    debug('intersection: entry', {
                        isIntersecting: entry.isIntersecting,
                        intersectionRatio: entry.intersectionRatio,
                        editorHasFocus: this.editorHasFocus,
                    });
                    // When sentinel is not intersecting (scrolled out of view), make toolbar sticky
                    // The sentinel is above the toolbar, so when it's gone, toolbar should stick
                    if (this.editorHasFocus) {
                        if (!entry.isIntersecting) {
                            this.isSticky = true;
                            if (this.toolbarElement) {
                                const rect = this.toolbarElement.nativeElement.getBoundingClientRect();
                                this.toolbarWidth = `${rect.width}px`;
                                const containerTop =
                                    this.scrollContainer instanceof Window
                                        ? 0
                                        : (this.scrollContainer as HTMLElement).getBoundingClientRect().top;
                                const stickyTop = this.getStickyTop(containerTop);
                                this.toolbarTop = `${stickyTop}px`;
                                debug('intersection: set sticky', {
                                    width: this.toolbarWidth,
                                    top: this.toolbarTop,
                                    containerTop,
                                    stickyTop,
                                });
                            }
                        } else {
                            this.isSticky = false;
                            debug('intersection: unset sticky (sentinel visible)');
                        }
                    } else {
                        this.isSticky = false;
                        debug('intersection: unset sticky (editor not focused)');
                    }

                    this.updateTableToolbarPosition('intersection');
                });
            },
            {
                root: scrollContainer === document.body ? null : scrollContainer,
                threshold: [0],
                rootMargin: '0px',
            }
        );

        debug('setupIntersectionObserver: observe sentinel');

        this.intersectionObserver.observe(this.sentinelElement.nativeElement);
    }

    private findScrollContainer(element: HTMLElement): HTMLElement | null {
        let parent = element.parentElement;

        while (parent && parent !== document.body) {
            const computedStyle = window.getComputedStyle(parent);
            const overflowY = computedStyle.overflowY;
            const overflowX = computedStyle.overflowX;

            const canScrollY = parent.scrollHeight > parent.clientHeight;
            const canScrollX = parent.scrollWidth > parent.clientWidth;

            if (
                overflowY === 'auto' ||
                overflowY === 'scroll' ||
                overflowX === 'auto' ||
                overflowX === 'scroll'
            ) {
                if (canScrollY || canScrollX) {
                    debug('findScrollContainer: found', {
                        tagName: parent.tagName,
                        className: parent.className,
                        overflowY,
                        overflowX,
                        canScrollY,
                        canScrollX,
                    });
                    return parent;
                }

                debug('findScrollContainer: skip non-scrollable', {
                    tagName: parent.tagName,
                    className: parent.className,
                    overflowY,
                    overflowX,
                    canScrollY,
                    canScrollX,
                });
            }

            parent = parent.parentElement;
        }

        debug('findScrollContainer: default document.body');
        return document.body;
    }

    private setupScrollListener(): void {
        if (!this.scrollContainer) {
            debug('setupScrollListener: missing scrollContainer');
            return;
        }

        const target = this.scrollContainer instanceof Window ? window : this.scrollContainer;
        this.scrollListener = () => {
            if (this.scrollRafId) {
                return;
            }
            this.scrollRafId = requestAnimationFrame(() => {
                this.scrollRafId = undefined;
                debug('scroll: event', {
                    source: this.scrollContainer instanceof Window ? 'window' : 'container',
                    scrollTop:
                        this.scrollContainer instanceof Window
                            ? window.scrollY
                            : (this.scrollContainer as HTMLElement).scrollTop,
                });
                this.updateStickyFromScroll('scroll');
                this.updateTableToolbarPosition('scroll');
            });
        };

        target.addEventListener('scroll', this.scrollListener, { passive: true });
        debug('setupScrollListener: attached', {
            isWindow: this.scrollContainer instanceof Window,
        });

        this.updateStickyFromScroll('init');
    }

    private updateStickyFromScroll(source: 'init' | 'focus' | 'blur' | 'scroll'): void {
        if (!this.toolbarElement || !this.sentinelElement) {
            debug('updateStickyFromScroll: missing elements', { source });
            return;
        }

        const editorElement = this.editorElement ?? this.getEditorElement();
        if (!editorElement) {
            debug('updateStickyFromScroll: editor element not found', { source });
            return;
        }
        this.editorElement = editorElement;

        const containerTop =
            this.scrollContainer instanceof Window
                ? 0
                : (this.scrollContainer as HTMLElement).getBoundingClientRect().top;
        const sentinelRect = this.sentinelElement.nativeElement.getBoundingClientRect();

        const containerBottom =
            this.scrollContainer instanceof Window
                ? window.innerHeight
                : (this.scrollContainer as HTMLElement).getBoundingClientRect().bottom;
        const editorRect = editorElement.getBoundingClientRect();
        const editorVisible =
            this.editorVisibilityObserver
                ? this.isToolbarVisible
                : editorRect.bottom > containerTop && editorRect.top < containerBottom;
        if (!this.editorVisibilityObserver) {
            this.isToolbarVisible = editorVisible;
        }

        const stickyTop = this.getStickyTop(containerTop);
        const shouldStick = this.editorHasFocus && editorVisible && sentinelRect.top <= stickyTop;
        const nextSticky = shouldStick;

        debug('updateStickyFromScroll: evaluate', {
            source,
            isSticky: this.isSticky,
            editorHasFocus: this.editorHasFocus,
            sentinelTop: sentinelRect.top,
            containerTop,
        });

        if (this.isSticky !== nextSticky) {
            this.isSticky = nextSticky;
            debug('updateStickyFromScroll: toggle', {
                source,
                isSticky: this.isSticky,
                editorHasFocus: this.editorHasFocus,
                sentinelTop: sentinelRect.top,
                containerTop,
            });
        }

        if (this.isSticky) {
            const rect = this.toolbarElement.nativeElement.getBoundingClientRect();
            this.toolbarWidth = `${rect.width}px`;
            this.toolbarTop = `${stickyTop}px`;
            debug('updateStickyFromScroll: sticky styles', {
                width: this.toolbarWidth,
                top: this.toolbarTop,
                containerTop,
                stickyTop,
            });
        }
    }

    private setupEditorVisibilityObserver(): void {
        if (!this.editorElement || !this.scrollContainer) {
            return;
        }

        if (this.editorVisibilityObserver) {
            this.editorVisibilityObserver.disconnect();
        }

        const root = this.scrollContainer === window ? null : (this.scrollContainer as HTMLElement);
        this.editorVisibilityObserver = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const isVisible = entry.isIntersecting;
                    if (this.isToolbarVisible !== isVisible) {
                        this.isToolbarVisible = isVisible;
                        if (!isVisible) {
                            this.isSticky = false;
                        }
                        debug('editor visibility: update', { isVisible });
                        this.updateStickyFromScroll('scroll');
                        this.updateTableToolbarPosition('scroll');
                    }
                });
            },
            {
                root,
                threshold: [0],
            }
        );

        this.editorVisibilityObserver.observe(this.editorElement);
    }

    @HostListener('window:resize')
    onResize(): void {
        if (this.isSticky && this.toolbarElement) {
            const rect = this.toolbarElement.nativeElement.getBoundingClientRect();
            this.toolbarWidth = `${rect.width}px`;
            const containerTop =
                this.scrollContainer instanceof Window
                    ? 0
                    : (this.scrollContainer as HTMLElement).getBoundingClientRect().top;
            const stickyTop = this.getStickyTop(containerTop);
            this.toolbarTop = `${stickyTop}px`;
            debug('onResize: update sticky styles', {
                width: this.toolbarWidth,
                top: this.toolbarTop,
                containerTop,
                stickyTop,
            });
        }

        this.updateTableToolbarPosition('resize');
    }

    private getStickyTop(containerTop: number): number {
        const baseTop = Math.max(containerTop, 0);
        const headerOffset = this.getHeaderOffset(containerTop);
        return baseTop + headerOffset;
    }

    private getHeaderOffset(containerTop: number): number {
        const extraOffset = Number(this.headerOffsetPx) || 0;
        const selector = this.headerOffsetSelector?.trim();

        if (!selector) {
            return Math.max(extraOffset, 0);
        }

        if (this.headerSelectorUsed !== selector) {
            this.headerSelectorUsed = selector;
            this.headerElement = undefined;
            this.headerLookupAttempts = 0;
            if (this.headerLookupTimer) {
                window.clearTimeout(this.headerLookupTimer);
                this.headerLookupTimer = undefined;
            }
        }

        if (this.headerElement && this.headerElement.isConnected) {
            const rect = this.headerElement.getBoundingClientRect();
            return Math.max(rect.bottom - containerTop + extraOffset, 0);
        }

        const header = document.querySelector(selector) as HTMLElement | null;
        if (!header) {
            debug('getHeaderOffset: header not found', { selector, extraOffset });
            this.scheduleHeaderLookup(selector);
            return Math.max(extraOffset, 0);
        }

        this.headerElement = header;
        const rect = header.getBoundingClientRect();
        const offset = Math.max(rect.bottom - containerTop + extraOffset, 0);
        debug('getHeaderOffset: header bottom offset', {
            selector,
            extraOffset,
            offset,
        });
        return offset;
    }

    private scheduleHeaderLookup(selector: string): void {
        if (!selector) {
            return;
        }

        if (this.headerElement && this.headerElement.isConnected) {
            return;
        }

        if (this.headerLookupTimer || this.headerLookupAttempts >= this.headerLookupMaxAttempts) {
            return;
        }

        this.headerLookupAttempts += 1;
        this.headerLookupTimer = window.setTimeout(() => {
            this.headerLookupTimer = undefined;
            const header = document.querySelector(selector) as HTMLElement | null;
            if (header) {
                this.headerElement = header;
                debug('header lookup: found', { selector });
                this.updateStickyFromScroll('init');
                return;
            }

            debug('header lookup: retry', {
                selector,
                attempt: this.headerLookupAttempts,
            });
            this.scheduleHeaderLookup(selector);
        }, this.headerLookupDelayMs);
    }

    private updateTableToolbarPosition(
        source: 'init' | 'focus' | 'blur' | 'scroll' | 'resize' | 'intersection' | 'selection',
    ) {
        if (this.tableToolbarRafId) {
            return;
        }

        this.tableToolbarRafId = requestAnimationFrame(() => {
            this.tableToolbarRafId = undefined;
            const surface = this.surfaceElement?.nativeElement;
            const toolbar = this.tableToolbarElement?.nativeElement;

            if (!surface || !toolbar) {
                return;
            }

            if (!this.isToolbarVisible || !this.editor || !this.isInTable()) {
                this.tableToolbarTop = 0;
                this.setTableToolbarVisible(false, 'not-in-table');
                return;
            }

            const table = this.getActiveTableElement();
            if (!table) {
                this.tableToolbarTop = 0;
                this.setTableToolbarVisible(false, 'no-table');
                return;
            }

            const surfaceRect = surface.getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            const toolbarHeight = toolbar.getBoundingClientRect().height;
            const relativeTop = tableRect.top - surfaceRect.top;
            this.tableToolbarTop = Math.max(relativeTop - toolbarHeight - this.tableToolbarGap, 0);

            this.setTableToolbarVisible(true, source);

            // Determine if table toolbar should become sticky below the main toolbar
            const mainToolbar = this.toolbarElement?.nativeElement;
            if (this.isSticky && mainToolbar) {
                const mainToolbarRect = mainToolbar.getBoundingClientRect();
                const naturalTop = tableRect.top - toolbarHeight - this.tableToolbarGap;
                // If the natural position is above the main toolbar bottom, stick it
                if (naturalTop < mainToolbarRect.bottom && tableRect.bottom > mainToolbarRect.bottom + toolbarHeight) {
                    this.isTableToolbarSticky = true;
                    this.tableToolbarStickyTop = `${mainToolbarRect.bottom}px`;
                    this.tableToolbarStickyLeft = `${surfaceRect.left}px`;
                    this.tableToolbarStickyWidth = `${surfaceRect.width}px`;
                } else {
                    this.isTableToolbarSticky = false;
                }
            } else {
                this.isTableToolbarSticky = false;
            }

            debug('tableToolbar: position', {
                source,
                tableTop: tableRect.top,
                surfaceTop: surfaceRect.top,
                toolbarHeight,
                tableToolbarTop: this.tableToolbarTop,
                isTableToolbarSticky: this.isTableToolbarSticky,
            });
        });
    }

    private getActiveTableElement(): HTMLElement | null {
        if (!this.editor?.view) {
            return null;
        }

        const { from } = this.editor.state.selection;
        const domAtPos = this.editor.view.domAtPos(from);
        const domNode = domAtPos.node as HTMLElement | null;
        if (!domNode) {
            return null;
        }

        const element = domNode.nodeType === Node.TEXT_NODE ? domNode.parentElement : domNode;
        return (element?.closest('table') as HTMLElement) ?? null;
    }

    private setTableToolbarVisible(isVisible: boolean, reason: string): void {
        if (isVisible) {
            if (this.tableToolbarHideTimer) {
                window.clearTimeout(this.tableToolbarHideTimer);
                this.tableToolbarHideTimer = undefined;
            }
            if (!this.isTableToolbarVisible) {
                debug('tableToolbar: show', { reason });
            }
            this.isTableToolbarVisible = true;
            return;
        }

        if (this.tableToolbarHideTimer) {
            return;
        }

        this.tableToolbarHideTimer = window.setTimeout(() => {
            this.tableToolbarHideTimer = undefined;
            if (this.isTableToolbarVisible) {
                debug('tableToolbar: hide', { reason });
            }
            this.isTableToolbarVisible = false;
        }, 160);
    }

    onSurfacePointer(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target) {
            return;
        }

        const overTable = !!target.closest('table');
        const overToolbar = !!this.tableToolbarElement?.nativeElement.contains(target);
        if (overTable || overToolbar) {
            this.setTableToolbarVisible(true, 'hover');
        }
    }

    onSurfaceLeave(): void {
        this.setTableToolbarVisible(false, 'leave');
    }

    @HostListener('mousedown', ['$event'])
    onMouseDown(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        if (!target || !this.toolbarElement) {
            return;
        }

        if (this.toolbarElement.nativeElement.contains(target) ||
            this.tableToolbarElement?.nativeElement?.contains(target)) {
            this.isInteractingWithToolbar = true;
            debug('toolbar: mousedown, hold focus state');
        }
    }

    @HostListener('mouseup', ['$event'])
    onMouseUp(event: MouseEvent): void {
        if (!this.isInteractingWithToolbar) {
            return;
        }

        this.isInteractingWithToolbar = false;
        debug('toolbar: mouseup, release focus hold');
        this.focusEditor();
    }
}
